import { useEffect, useMemo } from 'react'
import { useGLTF, useTexture } from '@react-three/drei'
import { createContourTerrainMaterial } from './ContourTerrainMaterial.js'
import { createAerialContourTerrainMaterial, setAerialTerrainConditions } from './AerialContourTerrainMaterial.js'
import { useExpedition } from '../store/useExpedition.js'

function prepareTerrain(scene, material, name) {
  scene.traverse(object => {
    if (!object.isMesh) return
    object.name = name
    object.userData.terrainSurface = name === 'BoulderTerrainLocal'
    object.castShadow = false
    object.receiveShadow = true
    object.material = material
  })
}

/**
 * Drop-in V5 replacement for V4 Terrain.jsx. Bounds below are in final world-space,
 * after the parent group/local mesh transforms used by V4.
 */
export function ContourTerrain({ lowPower = false }) {
  const weatherPhase=useExpedition(state=>state.weatherPhase)
  const timeOfDay=useExpedition(state=>state.timeOfDay)
  const localGltf = useGLTF(lowPower
    ? './terrain/boulder-foothills-mobile.glb'
    : './terrain/boulder-foothills-desktop.glb')
  const rangeGltf = useGLTF('./terrain/front-range-backdrop.glb')
  const aerial = useTexture(lowPower
    ? './terrain/aerial/boulder-usgs-imagery-mobile.jpg'
    : './terrain/aerial/boulder-usgs-imagery-desktop.jpg')
  const local = useMemo(() => localGltf.scene.clone(true), [localGltf.scene])
  const range = useMemo(() => rangeGltf.scene.clone(true), [rangeGltf.scene])
  const localMaterial = useMemo(() => createAerialContourTerrainMaterial(aerial, {
    forest:'#3f5046', granite:'#85857d', deepNavy:'#071738', contour:'#839ca3', rust:'#a45b3a', ivory:'#e5e1d7',
    elevationMin: -5.2,
    elevationMax: 23.5,
    contourSpacing: lowPower ? 0.84 : 0.58,
    contourWidth:0.026,
    anisotropy:lowPower?2:8,
    aerialStrength:lowPower?.72:.91,
    aerialNear:30,
    aerialFar:lowPower?90:132,
    detailStrength:lowPower?.28:.68,
    contourNear:lowPower?58:74,
    contourFar:lowPower?118:162,
    rustSlopeStart:.55,
    snowStart:.84,
    sunDirection:[-.6,.7,.38],
  }), [aerial, lowPower])
  const rangeMaterial = useMemo(() => createContourTerrainMaterial({
    navy:'#647069', deepNavy:'#314143', contourBlue:'#90aeb8', rust:'#8c5940', ivory:'#e7e5dd',
    elevationMin: -7,
    elevationMax: 32,
    contourSpacing: 1.35,
    contourWidth: 0.018,
    rustSlopeStart: 0.58,
    snowStart: 0.69,
    inkStrength: 0.08,
    distanceFade: 150,
  }), [])

  // Synchronous naming allows route projection in a sibling committed the same frame.
  prepareTerrain(local, localMaterial, 'BoulderTerrainLocal')
  prepareTerrain(range, rangeMaterial, 'BoulderTerrainRange')

  useEffect(() => () => {
    localMaterial.dispose()
    rangeMaterial.dispose()
  }, [localMaterial, rangeMaterial])
  useEffect(()=>{
    const severe=weatherPhase==='whiteout' ? 1 : weatherPhase==='front-arriving' ? .72 : weatherPhase==='snow-grains' ? .5 : .08
    setAerialTerrainConditions(localMaterial,{weather:severe,timeOfDay,visibility:1-severe*.65,snowStart:weatherPhase==='whiteout' ? .68 : .84})
  },[localMaterial,timeOfDay,weatherPhase])

  return <group position={[0, -5.4, -18]} scale={3.2}>
    <primitive object={range} position={[0, -0.4, -19]} scale={[1.55, 1.8, 1.55]} />
    <primitive object={local} position={[0, 0.04, 3]} scale={[1, 2.05, 1]} />
  </group>
}
